// Endpoint:  POST /{organization_id}/brand_kits
// Schema:    CreateBrandKitRequest
//
// Required: name, palette
// Palette required: primary_color
// Palette optional: secondary_color, subtitle_color, highlight_color,
//                   title_color, subtitle_stroke_color, title_stroke_color

import { useMemo, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import type { RequestSpec } from '@/types';
import type { PlaygroundProps } from './index';
import { Field, FormCard, OrgIdRequired, PlaygroundPanel, TextInput } from './_shared';

interface PaletteState {
  primary_color: string;
  secondary_color: string;
  subtitle_color: string;
  highlight_color: string;
  title_color: string;
  subtitle_stroke_color: string;
  title_stroke_color: string;
}

export function BrandKitPlayground({ mode }: PlaygroundProps) {
  const { organizationId } = useAuth();

  const [name, setName] = useState('Acme Corp');
  const [workspaceId, setWorkspaceId] = useState('');
  const [palette, setPalette] = useState<PaletteState>({
    primary_color: '#2563eb',
    secondary_color: '#0ea5e9',
    subtitle_color: '',
    highlight_color: '#f59e0b',
    title_color: '#0f172a',
    subtitle_stroke_color: '',
    title_stroke_color: '',
  });

  const updateColor = (key: keyof PaletteState, value: string) =>
    setPalette((p) => ({ ...p, [key]: value }));

  const spec: RequestSpec = useMemo(() => {
    const orgSegment = organizationId || '{organization_id}';
    const cleanedPalette: Record<string, string> = {
      primary_color: palette.primary_color,
    };
    for (const k of [
      'secondary_color',
      'subtitle_color',
      'highlight_color',
      'title_color',
      'subtitle_stroke_color',
      'title_stroke_color',
    ] as const) {
      if (palette[k]) cleanedPalette[k] = palette[k];
    }
    const body: Record<string, unknown> = {
      name,
      palette: cleanedPalette,
    };
    if (workspaceId) body.workspace_id = workspaceId;
    return {
      method: 'POST',
      path: `/media/${orgSegment}/brand_kits`,
      body,
    };
  }, [organizationId, name, workspaceId, palette]);

  return (
    <PlaygroundPanel
      title="Create a brand kit"
      description="Brand kits hold a color palette (and, via separate endpoints, fonts and assets like logos) that gets applied to generated experiences."
      spec={spec}
      mode={mode}
      runLabel={`Send POST /${organizationId || '{organization_id}'}/brand_kits`}
    >
      <OrgIdRequired organizationId={organizationId} />
      <FormCard>
        <Field label="Name" hint="Required">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Workspace ID" hint="Optional — defaults to organization's primary workspace">
          <TextInput value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} placeholder="ws_…" />
        </Field>

        <div>
          <div className="lp-text-xs lp-font-semibold lp-text-ink-500 lp-uppercase lp-tracking-wider lp-mb-2">
            Palette <span className="lp-font-mono lp-text-ink-400 lp-normal-case">CreatePaletteRequest</span>
          </div>
          <div className="lp-grid lp-grid-cols-2 lp-gap-3">
            <ColorField label="Primary *" value={palette.primary_color} onChange={(v) => updateColor('primary_color', v)} />
            <ColorField label="Secondary" value={palette.secondary_color} onChange={(v) => updateColor('secondary_color', v)} />
            <ColorField label="Highlight" value={palette.highlight_color} onChange={(v) => updateColor('highlight_color', v)} />
            <ColorField label="Title" value={palette.title_color} onChange={(v) => updateColor('title_color', v)} />
            <ColorField label="Subtitle" value={palette.subtitle_color} onChange={(v) => updateColor('subtitle_color', v)} />
            <ColorField label="Title stroke" value={palette.title_stroke_color} onChange={(v) => updateColor('title_stroke_color', v)} />
            <ColorField label="Subtitle stroke" value={palette.subtitle_stroke_color} onChange={(v) => updateColor('subtitle_stroke_color', v)} />
          </div>
        </div>
      </FormCard>
    </PlaygroundPanel>
  );
}

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="lp-block">
      <div className="lp-text-xs lp-font-mono lp-text-ink-500">{label}</div>
      <div className="lp-mt-1.5 lp-flex lp-gap-2 lp-items-center">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="lp-h-9 lp-w-12 lp-rounded-lg lp-border lp-border-ink-200 lp-cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#rrggbb"
          className="lp-flex-1 lp-bg-white lp-border lp-border-ink-200 lp-rounded-xl lp-px-3 lp-py-2 lp-text-sm lp-font-mono"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="lp-text-ink-400 hover:lp-text-rose-600 lp-px-1"
            aria-label="Clear color"
          >
            ×
          </button>
        )}
      </div>
    </label>
  );
}
